import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectCreditCardsFullPaginatedData,
  selectErrorDetailMapFromState,
  selectFilteredCreditCardOptions,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectPostingCreditCardFromState,
  selectTopLevelErrorFromState,
  selectUsersFullPaginatedData,
} from 'src/app/RequestBudget/redux/requestBudget.selectors'
import BudgetPickContent from 'src/components/BudgetPickContent/BudgetPickContent'
import BudgetRecurringContent from 'src/components/BudgetRecurringContent/BudgetRecurringContent'
import BudgetStandardContent from 'src/components/BudgetStandardContent/BudgetStandardContent'
import { CreditCardDropdownInfiniteScrollProps } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useFlyoutDispatch } from 'src/hooks'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import {
  getCreditCard,
  getCreditCards,
  postBudgetRequestAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'

export enum BudgetScreen {
  Pick,
  Standard,
  Recurring,
}

const RequestBudget: React.FC = () => {
  const postingCreditCard = useSelector(selectPostingCreditCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const queryParams = useSelector(selectQueryParams)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCardOptions = useSelector(selectFilteredCreditCardOptions)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)

  const dispatch = useFlyoutDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostCreditCardAndOpenToastAndCloseFlyout = (
    body: Partial<PostCreditCardRequest>,
  ): void => {
    dispatch(postBudgetRequestAndOpenToastAndCloseFlyout(body))
  }
  const [screen, setScreen] = useState(BudgetScreen.Pick)

  useEffect((): void => {
    if (typeof queryParams['creditCardId'] === 'string') {
      dispatch(getCreditCard(queryParams['creditCardId']))
    }
  }, [queryParams['creditCardId']])

  const initialFlatFields = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const fetchCreditCards = (filterInput?: string, page?: number): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        sortField: 'type',
        sortDirection: 'ASC',
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueDelegateCreditCards,
        budgetsEnabled: true,
      }),
    )
  }
  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: fetchCreditCards,
    ...creditCardsFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {!statisticsLoaded.creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsLoaded ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <Helmet>
            <title>{`Request a Budget | ${appName}`}</title>
            <meta
              name="description"
              content={`Request a Budget page of ${appName}`}
            />
          </Helmet>
          {screen === BudgetScreen.Pick && (
            <BudgetPickContent setScreen={setScreen} requesting />
          )}
          {screen === BudgetScreen.Standard && (
            <BudgetStandardContent
              postCreditCardOrBudgetRequest={
                dispatchPostCreditCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
              requesting
            />
          )}
          {screen === BudgetScreen.Recurring && (
            <BudgetRecurringContent
              postCreditCard={dispatchPostCreditCardAndOpenToastAndCloseFlyout}
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
              requesting
            />
          )}
        </>
      )}
    </RightFlyout>
  )
}

export default RequestBudget
